// The on-device database itself (IndexedDB): its shape, opening it, the
// "something changed" signal screens re-read on, and queueing a change to sync.
// Used only by device-db.ts (what screens do) and device-sync.ts (what sync
// does) — screens never come here directly.
//
// Each edit writes the record AND its outbox entry in one transaction, so a
// change can never be saved without also being queued to sync (or vice versa).
// queue() therefore takes the caller's transaction; it never opens its own.
import { openDB, type DBSchema, type IDBPDatabase, type IDBPTransaction, type StoreNames } from 'idb';
import type { AccessTag, SurfaceType } from '@higgins/m2q-pricing';
import type { Direction } from './compass';
import type { Position } from './location';
import type { ConditionContractSummary, JobDetails } from './types';

const DB_NAME = 'measure-to-quote';
/** 1: jobs, records, photos, outbox (2026-10-07). 2: reference data (2026-10-08). 3: site plans (2026-10-21). */
const DB_VERSION = 3;

/**
 * The tables the device works with; the API maps them to Dataverse. `photo` is one row per photo (photo-rows.ts);
 * visit … visitareatag are condition reporting; the tag tables are a row per tick (tick-rows.ts).
 * `evidencephoto` is a condition photo not yet moved to a photo row (the API sends it until the migration has run).
 */
export type DeviceTable =
  | 'measure'
  | 'building'
  | 'area'
  | 'areatag'
  | 'version'
  | 'option'
  | 'jobprice'
  | 'reviewanswer'
  | 'photo'
  | 'visit'
  | 'visitarea'
  | 'observation'
  | 'evidencephoto'
  | 'visitareatag'
  | 'defect';
type FieldValue = string | number | boolean | null;
export type Fields = Record<string, FieldValue>;

export interface DeviceJob {
  id: string;
  number: string;
  sharePointSite: string | null;
  details?: JobDetails;
  /** The client's Condition contract; absent on a job copied before it was sent. */
  conditionContract?: ConditionContractSummary | null;
  /** Photos removed in CRM (their rows made inactive), so a list from before the rows never shows them again. */
  removedPhotoIds?: string[];
  openedAt: string;
  lastSyncedAt: string | null;
  /** When it was last opened on this device, sent for the Recent jobs list (#422). Absent until opened again. */
  lastOpenedAt?: string;
}

/** synced = matches CRM · pending = waiting to sync · conflict = changed elsewhere, not overwritten. */
type RecordState = 'synced' | 'pending' | 'conflict';

export interface DeviceRecord {
  id: string;
  table: DeviceTable;
  jobId: string;
  parentId: string;
  fields: Fields;
  /** The CRM etag last seen; null = made on this device and not yet in CRM. */
  etag: string | null;
  state: RecordState;
  /** Bumped on every local edit, so a sync can tell whether the record moved on while it was sending. */
  revision: number;
  /** CRM refused this change (403: the job doesn't open for this user, or their role doesn't allow it; 404: no longer in CRM). Set aside until tried again or discarded (refused.ts). */
  refusedReason?: string;
  /** A photo row being removed: hidden at once, kept (and kept from a refresh) until CRM has it, then forgotten. */
  removing?: true;
}

export interface DevicePhoto {
  id: string;
  areaId: string;
  jobId: string;
  /** The surface (in the area's hig_surfaces) it's a photo of; null or absent = the area in general. */
  surfaceId?: string | null;
  /** Taken as the area's own perspective shot (#437); absent = not. */
  areaPhoto?: boolean;
  /** Fixed when the photo is taken, so a re-sent upload overwrites rather than duplicates. */
  fileName: string;
  /** The full-size JPEG while it waits to upload; null once it's on SharePoint (OFFLINE.md: full size on demand). */
  image: Blob | null;
  /**
   * A small JPEG as a data: URL — text, not a Blob, because Safari can fail to
   * show a Blob for a moment after its record is rewritten (2026-10-07 iPad test).
   * Photos taken before that fix hold a Blob here.
   */
  thumbnail: string | Blob;
  state: 'waiting' | 'uploaded';
  takenAt: string;
  /** The compass bearing when it was taken (0–359, 0 = north); null = no compass. */
  bearing?: number | null;
  /** The way the camera faced, N … NW: from the bearing, or set by hand. */
  direction?: Direction | null;
  /** Where it was taken, for the site plan; null or absent = no position fix (or taken before 2026-10-21). */
  position?: Position | null;
  /** The upload (or its new direction) was refused, as for a record (DeviceRecord.refusedReason). */
  refusedReason?: string;
}

export interface OutboxEntry {
  seq?: number;
  /**
   * photo-meta: an uploaded photo's direction changed, to send to its area's list (the build before photo rows).
   * photo-remove: a photo removed. opened: the job was opened (Recent jobs, #422).
   */
  kind: 'record' | 'photo' | 'photo-meta' | 'photo-remove' | 'opened';
  /** The record id or photo id. The payload is read fresh at send time, so edits coalesce. */
  targetId: string;
  jobId: string;
  queuedAt: string;
  attempts: number;
  lastError: string | null;
}

/** Reference lists copied from CRM so measuring works offline. Read-only on the device; one entry per list. */
export interface ReferenceLists {
  surfaceTypes: SurfaceType[];
  accessTags: AccessTag[];
}
type ReferenceData = Partial<ReferenceLists> & { key: keyof ReferenceLists; fetchedAt: string };

/**
 * A visit's site plan image, kept so the plan shows with no signal. A JPEG as
 * a data: URL (text, not a Blob — Safari can fail to show a Blob just read
 * back, 2026-10-07 iPad test). Its one copy of record is on SharePoint.
 */
export interface SitePlanImage {
  visitId: string;
  /** Which plan it's a copy of (site-plan.ts planVersion): a newer plan, from here or another device, replaces it. */
  version: string;
  dataUrl: string;
}

interface M2qDb extends DBSchema {
  jobs: { key: string; value: DeviceJob };
  records: { key: string; value: DeviceRecord; indexes: { byJob: string } };
  photos: { key: string; value: DevicePhoto; indexes: { byArea: string; byJob: string } };
  outbox: { key: number; value: OutboxEntry; indexes: { byTarget: string; byJob: string } };
  reference: { key: string; value: ReferenceData };
  siteplans: { key: string; value: SitePlanImage };
}

let dbPromise: Promise<IDBPDatabase<M2qDb>> | null = null;

export function db(): Promise<IDBPDatabase<M2qDb>> {
  dbPromise ??= openDB<M2qDb>(DB_NAME, DB_VERSION, {
    // Each step runs once per device, from the version it already has — an
    // iPad on version 1 keeps its jobs and waiting changes and only gains the new store.
    upgrade(database, oldVersion) {
      if (oldVersion < 1) {
        database.createObjectStore('jobs', { keyPath: 'id' });
        database.createObjectStore('records', { keyPath: 'id' }).createIndex('byJob', 'jobId');
        const photos = database.createObjectStore('photos', { keyPath: 'id' });
        photos.createIndex('byArea', 'areaId');
        photos.createIndex('byJob', 'jobId');
        const outbox = database.createObjectStore('outbox', { keyPath: 'seq', autoIncrement: true });
        outbox.createIndex('byTarget', 'targetId');
        outbox.createIndex('byJob', 'jobId');
      }
      if (oldVersion < 2) database.createObjectStore('reference', { keyPath: 'key' });
      if (oldVersion < 3) database.createObjectStore('siteplans', { keyPath: 'visitId' });
    },
  });
  return dbPromise;
}

// ---------- change notification, so screens re-read after any write ----------

const changes = new EventTarget();
const CHANGE = 'change';

export function notifyChange(): void {
  changes.dispatchEvent(new Event(CHANGE));
}

export function onDeviceChange(listener: () => void): () => void {
  changes.addEventListener(CHANGE, listener);
  return () => changes.removeEventListener(CHANGE, listener);
}

// ---------- queueing a change, inside the caller's transaction ----------

/** A change to the job, as opposed to a note that it was opened: only changes count as waiting to sync. */
export const isChange = (entry: OutboxEntry): boolean => entry.kind !== 'opened';

type WriteTx = IDBPTransaction<M2qDb, ArrayLike<StoreNames<M2qDb>>, 'readwrite'>;

export async function queue(tx: WriteTx, entry: Omit<OutboxEntry, 'queuedAt' | 'attempts' | 'lastError'>): Promise<void> {
  // One entry per target and kind: later edits ride on the entry already queued.
  // (A photo and its evidence record share an id, so the kind is part of it.)
  const queued = await tx.objectStore('outbox').index('byTarget').getAll(entry.targetId);
  if (queued.some((e) => e.kind === entry.kind)) return;
  await tx.objectStore('outbox').add({ ...entry, queuedAt: new Date().toISOString(), attempts: 0, lastError: null });
}
